import { useState, useEffect, createContext, useContext } from 'react';
import { supabase } from '../lib/supabase';
import { Tenant, TenantUser, ITenantContext } from '../types/tenant';
import { Database } from '../lib/database.types';

// Utility function to transform flat database structure to nested Tenant interface
const transformDbTenantToTenant = (dbTenant: Database['public']['Tables']['tenants']['Row']): Tenant => {
  return {
    id: dbTenant.id,
    name: dbTenant.name,
    subdomain: dbTenant.subdomain,
    custom_domain: dbTenant.custom_domain || undefined,
    plan: dbTenant.plan,
    status: dbTenant.status,
    settings: {
      branding: dbTenant.branding as any,
      features: dbTenant.features as any,
      limits: dbTenant.limits as any
    },
    billing: dbTenant.billing as any,
    createdAt: dbTenant.created_at,
    updatedAt: dbTenant.updated_at
  };
};

export const useTenantData = () => {
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [user, setUser] = useState<TenantUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    initializeTenant();
  }, []);

  const initializeTenant = async () => {
    try {
      setLoading(true);
      
      // Get current user session
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        setLoading(false);
        return;
      }

      // For localhost development, don't auto-create tenants
      // Let the tenant setup form handle tenant creation
      console.log('Tenant initialization skipped for localhost - use tenant setup form');

    } catch (error) {
      console.error('Error initializing tenant:', error);
    } finally {
      setLoading(false);
    }
  };

  const createDemoTenant = async (): Promise<Tenant> => {
    const tenantName = 'Demo Hospital';
    
    // Critical validation: ensure name is never null/undefined/empty
    if (!tenantName || typeof tenantName !== 'string' || tenantName.trim() === '') {
      console.error('Tenant name validation failed:', { tenantName });
      throw new Error('Tenant name cannot be null, undefined, or empty');
    }

    const demoTenant: Database['public']['Tables']['tenants']['Insert'] = {
      id: 'demo',
      name: tenantName,
      custom_domain: 'localhost',
      subdomain: 'demo',
      plan: 'professional',
      status: 'trial',
      branding: {
        primaryColor: '#2563eb',
        secondaryColor: '#1e40af',
        hospitalName: 'Demo Hospital'
      },
      features: {
        aiPredictions: true,
        automatedReminders: true,
        analytics: true,
        apiAccess: true,
        customIntegrations: false,
        whiteLabeling: false
      },
      limits: {
        maxUsers: 10,
        maxAppointments: 10000,
        maxPredictions: 1000,
        dataRetentionDays: 365
      },
      billing: {
        currentPeriodStart: new Date().toISOString(),
        currentPeriodEnd: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
        monthlyPrice: 0
      }
    };

    // Final validation before database operation
    if (!demoTenant.name || typeof demoTenant.name !== 'string' || demoTenant.name.trim() === '') {
      console.error('Final tenant validation failed:', { demoTenant });
      throw new Error('Tenant name validation failed before database operation');
    }

    console.log('Creating demo tenant with validated data:', { name: demoTenant.name, id: demoTenant.id });

    const { data, error } = await supabase
      .from('tenants')
      .upsert(demoTenant)
      .select()
      .maybeSingle();

    if (error) {
      console.error('Error creating demo tenant:', error);
      throw error;
    }

    if (!data) {
      throw new Error('Failed to create demo tenant');
    }

    return transformDbTenantToTenant(data);
  };

  const createTenantUser = async (tenantId: string, authUser: any): Promise<TenantUser> => {
    const email = authUser.email || '';
    let role: 'admin' | 'data-manager' | 'integration-user' = 'integration-user';
    
    if (email === 'admin@attendai.com') role = 'admin';
    else if (email === 'data@attendai.com') role = 'data-manager';

    const tenantUser: TenantUser = {
      id: authUser.id,
      tenantId,
      email,
      name: email.split('@')[0],
      role,
      permissions: getPermissionsForRole(role),
      createdAt: new Date().toISOString()
    };

    const { data, error } = await supabase
      .from('tenant_users')
      .upsert(tenantUser)
      .select()
      .maybeSingle();

    return data || tenantUser;
  };

  const getPermissionsForRole = (role: string): string[] => {
    switch (role) {
      case 'admin':
        return ['*']; // All permissions
      case 'data-manager':
        return ['upload_data', 'run_predictions', 'view_analytics', 'manage_appointments'];
      case 'integration-user':
        return ['view_dashboard', 'view_analytics'];
      default:
        return ['view_dashboard'];
    }
  };

  const switchTenant = async (tenantId: string) => {
    // Implementation for switching between tenants (for super admins)
    setLoading(true);
    try {
      const { data: tenantData } = await supabase
        .from('tenants')
        .select('*')
        .eq('id', tenantId)
        .maybeSingle();
      
      if (tenantData) {
        setTenant(transformDbTenantToTenant(tenantData));
        // Update URL to reflect tenant switch
        window.location.href = `https://${tenantData.subdomain}.attendai.io`;
      }
    } finally {
      setLoading(false);
    }
  };

  return {
    tenant,
    user,
    loading,
    switchTenant
  };
};